import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Reveal } from "../components/Reveal";
import { ORG } from "../lib/constants";
import { submitApplication, uploadFile, apiError } from "../lib/api";
import { ArrowLeft, ArrowRight, CheckCircle2, Shield, Upload, FileText, X } from "lucide-react";

const fieldCls =
  "w-full bg-brand-cream border border-brand-line rounded-xl px-4 py-3 text-brand-charcoal placeholder-brand-muted/50 focus:outline-none focus:ring-2 focus:ring-brand-teal/25 focus:border-brand-teal transition-all";
const selectCls = fieldCls + " appearance-none pr-8";

function Field({ label, children, required, hint }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-brand-muted mb-1.5 tracking-wide">
        {label}{required && <><span aria-hidden="true" className="text-brand-rose"> *</span><span className="sr-only"> (required)</span></>}
      </span>
      {children}
      {hint && <span className="block text-[11px] text-brand-muted mt-1">{hint}</span>}
    </label>
  );
}

const UPLOAD_CATEGORIES = [
  { key: "id", label: "ID / Driver's License", hint: "Photo or scan of valid ID" },
  { key: "cdib", label: "CDIB Card", hint: "If applicable" },
  { key: "doc", label: "DOC / Release Paperwork", hint: "If coming from incarceration" },
  { key: "referral", label: "Referral Letter", hint: "From counselor, pastor, or caseworker" },
  { key: "other", label: "Other Supporting Documents", hint: "Any additional paperwork" },
];

const ACCEPT_TYPES = ".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp";
const MAX_SIZE = 10 * 1024 * 1024;

function UploadsStep({ data, setData }) {
  const [uploading, setUploading] = useState(null); // category key while uploading
  const [progress, setProgress] = useState(0);

  const onPick = (category) => async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    for (const f of files) {
      if (f.size > MAX_SIZE) {
        toast.error(`${f.name} is larger than 10 MB.`);
        continue;
      }
      try {
        setUploading(category);
        setProgress(0);
        const res = await uploadFile(f, category, setProgress);
        setData((d) => ({ ...d, uploads: [...d.uploads, res] }));
        toast.success(`Uploaded ${f.name}`);
      } catch (err) {
        toast.error(apiError(err, `Failed to upload ${f.name}`));
      } finally {
        setUploading(null);
        setProgress(0);
      }
    }
  };

  const remove = (i) =>
    setData((d) => ({ ...d, uploads: d.uploads.filter((_, idx) => idx !== i) }));

  return (
    <div className="space-y-5">
      <p className="text-sm text-brand-muted">
        Optional — you may skip this step. Accepted: PDF, JPG, PNG, WEBP (max 10 MB per file).
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {UPLOAD_CATEGORIES.map((c) => (
          <label
            key={c.key}
            className="group relative bg-brand-cream border-2 border-dashed border-brand-line rounded-2xl p-5 cursor-pointer hover:border-brand-teal hover:bg-brand-bone/50 focus-within:ring-2 focus-within:ring-brand-teal focus-within:border-brand-teal transition-colors block"
            data-testid={`upload-${c.key}`}
          >
            <div className="flex items-start gap-3">
              <span className="w-10 h-10 rounded-xl bg-brand-teal/10 text-brand-teal flex items-center justify-center shrink-0">
                <Upload className="w-4 h-4" />
              </span>
              <div className="min-w-0">
                <div className="font-medium text-brand-charcoal">{c.label}</div>
                <div className="text-xs text-brand-muted mt-0.5">{c.hint}</div>
                <div className="text-[11px] text-brand-teal mt-2 font-medium">
                  {uploading === c.key ? `Uploading… ${progress}%` : "Click to choose files"}
                </div>
              </div>
            </div>
            <input
              type="file"
              multiple
              accept={ACCEPT_TYPES}
              className="sr-only"
              disabled={!!uploading}
              onChange={onPick(c.key)}
            />
          </label>
        ))}
      </div>

      {data.uploads.length > 0 && (
        <div className="mt-6">
          <div className="text-xs font-medium tracking-wider uppercase text-brand-muted mb-3">
            Uploaded ({data.uploads.length})
          </div>
          <ul className="space-y-2">
            {data.uploads.map((u, i) => (
              <li
                key={u.path}
                className="flex items-center justify-between gap-3 bg-white border border-brand-line rounded-xl px-4 py-3"
                data-testid={`uploaded-file-${i}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="w-4 h-4 text-brand-teal shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm text-brand-charcoal truncate">{u.filename}</div>
                    <div className="text-[11px] text-brand-muted">
                      {u.category?.toUpperCase()} · {(u.size / 1024).toFixed(1)} KB
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  className="p-1.5 rounded-full text-brand-muted hover:text-brand-rose hover:bg-brand-rose/10 transition-colors"
                  aria-label={`Remove ${u.filename}`}
                  data-testid={`remove-uploaded-file-${i}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const STEPS = [
  { key: "basic", label: "Basic Info" },
  { key: "housing", label: "Housing History" },
  { key: "connections", label: "Connections" },
  { key: "medical", label: "Medical" },
  { key: "legal", label: "Legal" },
  { key: "personal", label: "Personal" },
  { key: "references", label: "References" },
  { key: "employment", label: "Employment" },
  { key: "financial", label: "Financial & Emergency" },
  { key: "uploads", label: "Supporting Documents" },
  { key: "agreement", label: "Review & Agreement" },
];

const emptyApp = {
  name: "", address: "", phone: "", dob: "", ssn: "", cdib_number: "", doc_number: "",
  ethnicity: "", marital_status: "", referral: "",
  housing_history: [
    { address: "", when: "", with_whom: "" },
    { address: "", when: "", with_whom: "" },
    { address: "", when: "", with_whom: "" },
  ],
  knows_local_people: "",
  known_contacts: [{ name: "", relation: "", address: "", phone: "" }],
  medications: [{ name: "", dosage: "" }, { name: "", dosage: "" }],
  pending_charges: "", criminal_history: "", probation_or_parole: "", officer_name: "", officer_phone: "",
  goals_next_year: "", how_we_can_help: "",
  has_children: "", num_children: "",
  children: [{ age: "", with_applicant: "", daycare: "" }],
  references: [
    { name: "", address: "", phone: "", relationship: "", years_known: "" },
    { name: "", address: "", phone: "", relationship: "", years_known: "" },
    { name: "", address: "", phone: "", relationship: "", years_known: "" },
  ],
  has_job: "", employer: "", length_of_employment: "", has_transportation: "",
  has_entry_fee: "", family_support: "",
  emergency_name: "", emergency_phone: "", emergency_relationship: "",
  signature: "", signature_date: new Date().toISOString().slice(0, 10),
  agreed_to_rules: false,
  privacy_ack: false,
  uploads: [],
  email: "",
  website: "",
};

export default function ApplicationPage() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(emptyApp);
  const [loading, setLoading] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);
  const [error, setError] = useState("");
  const headingRef = useRef(null);
  const firstRender = useRef(true);

  useEffect(() => { window.posthog?.stopSessionRecording?.(); }, []);
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    headingRef.current?.focus();
  }, [step]);

  const progress = useMemo(() => ((step + 1) / STEPS.length) * 100, [step]);

  const set = (k) => (e) =>
    setData((d) => ({ ...d, [k]: e.target?.type === "checkbox" ? e.target.checked : e.target.value }));

  const setNested = (listKey, i, field) => (e) =>
    setData((d) => ({
      ...d,
      [listKey]: d[listKey].map((row, idx) => (idx === i ? { ...row, [field]: e.target.value } : row)),
    }));

  const addRow = (listKey, template) =>
    setData((d) => ({ ...d, [listKey]: [...d[listKey], { ...template }] }));

  const removeRow = (listKey, i) =>
    setData((d) => ({ ...d, [listKey]: d[listKey].filter((_, idx) => idx !== i) }));

  const validateStep = () => {
    const s = STEPS[step].key;
    if (s === "basic") {
      if (!data.name.trim()) return "Please enter your full name.";
      if (!data.phone.trim()) return "A phone number helps us reach you.";
      if (!data.dob) return "Please enter your date of birth.";
    }
    if (s === "references") {
      const filled = data.references.filter((r) => r.name.trim() && (r.phone.trim() || r.address.trim()));
      if (filled.length < 1) return "Please add at least one reference (name + phone or address).";
    }
    if (s === "financial") {
      if (!data.emergency_name.trim() || !data.emergency_phone.trim())
        return "Please add an emergency contact (name and phone).";
    }
    if (s === "agreement") {
      if (!data.signature.trim()) return "Please type your name as your signature.";
      if (!data.agreed_to_rules) return "Please agree to the program rules to submit.";
      if (!data.privacy_ack) return "Please read and acknowledge the privacy notice to submit.";
    }
    return null;
  };

  const goTo = (i) => { setError(""); setStep(i); };
  const next = (e) => {
    e?.preventDefault();
    const err = validateStep();
    setError(err || "");
    if (err) return toast.error(err);
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const prev = () => {
    setError("");
    setStep((s) => Math.max(0, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validateStep();
    setError(err || "");
    if (err) return toast.error(err);
    setLoading(true);
    try {
      const res = await submitApplication(data);
      toast.dismiss();
      setSubmittedId(res.id);
      toast.success("Application submitted — we'll be in touch within 72 hours.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg = apiError(err, "Could not submit. Please try again.");
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (submittedId) {
    return (
      <div className="container-editorial py-24 md:py-32" data-testid="application-success">
        <div className="max-w-2xl mx-auto text-center bg-white rounded-3xl border border-brand-line p-10 md:p-14 shadow-sm">
          <CheckCircle2 className="w-14 h-14 text-brand-teal mx-auto" />
          <h1 className="mt-6 font-serif text-4xl md:text-5xl text-brand-charcoal tracking-tight">
            Thank you — your application is in.
          </h1>
          <p className="mt-5 text-brand-muted leading-relaxed">
            We received your application and will review it carefully. Please allow up to 72 hours for our team to
            respond. If it is urgent, call us at <a href={`tel:${ORG.phoneTel}`} className="text-brand-teal underline underline-offset-4">{ORG.phone}</a>.
          </p>
          <p className="mt-6 text-xs text-brand-muted">Reference: <span className="font-mono" data-testid="application-reference">{submittedId.slice(0, 8).toUpperCase()}</span></p>
          <a
            href="/"
            className="mt-8 inline-flex items-center gap-2 bg-brand-teal text-white rounded-full px-6 py-3 font-medium hover:bg-brand-tealDark transition-colors"
          >
            Return home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="application-page" className="ph-no-capture" data-private="true">
      <section className="pt-20 pb-10 md:pt-24 bg-gradient-to-br from-brand-cream via-brand-bone/30 to-brand-cream">
        <div className="container-editorial">
          <Reveal>
            <div className="max-w-3xl">
              <div className="text-[11px] tracking-[0.24em] uppercase text-brand-rose font-semibold">Application</div>
              <h1 className="mt-3 font-serif text-5xl md:text-6xl text-brand-charcoal tracking-tight leading-[1.04]">
                Start your year of restoration.
              </h1>
              <p className="mt-5 text-lg text-brand-muted leading-relaxed">
                Thank you for your interest in the Hosea House. Please complete each section below as thoroughly
                as you can. Your information is encrypted and only seen by authorized ministry staff.{" "}
                <Link to="/privacy" className="text-brand-teal underline underline-offset-4" data-testid="app-privacy-link-hero">Read our Privacy Notice</Link>.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-editorial">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            {/* Side step indicator */}
            <aside className="md:col-span-3">
              <div className="md:sticky md:top-28">
                <div className="text-[11px] tracking-[0.24em] uppercase text-brand-rose font-semibold mb-4">
                  Step {step + 1} of {STEPS.length}
                </div>
                <div className="h-2 bg-brand-line rounded-full overflow-hidden mb-6" role="progressbar"
                  aria-label="Application progress" aria-valuemin={1} aria-valuemax={STEPS.length} aria-valuenow={step + 1}>
                  <div
                    className="h-full bg-brand-teal transition-all duration-500"
                    style={{ width: `${progress}%` }}
                    data-testid="progress-fill"
                  />
                </div>
                <ol className="space-y-1.5 hidden md:block">
                  {STEPS.map((s, i) => (
                    <li key={s.key}>
                      <button
                        type="button"
                        onClick={() => goTo(i)}
                        aria-current={i === step ? "step" : undefined}
                        data-testid={`step-${s.key}`}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                          i === step
                            ? "bg-brand-teal text-white font-medium"
                            : i < step
                            ? "text-brand-teal hover:bg-brand-bone"
                            : "text-brand-muted hover:bg-brand-bone"
                        }`}
                      >
                        <span className="inline-block w-5 text-xs opacity-70 mr-1">{i + 1}.</span>
                        {s.label}
                      </button>
                    </li>
                  ))}
                </ol>
                <div className="mt-8 p-5 rounded-2xl bg-white border border-brand-line hidden md:block">
                  <div className="flex items-center gap-2 text-brand-teal">
                    <Shield className="w-4 h-4" />
                    <span className="text-xs font-semibold tracking-wider uppercase">Confidential</span>
                  </div>
                  <p className="text-xs text-brand-muted mt-2 leading-relaxed">
                    Sensitive details are encrypted, never emailed, and only used to review your application.{" "}
                    <Link to="/privacy" className="text-brand-teal underline underline-offset-2">Privacy Notice</Link>
                  </p>
                </div>
              </div>
            </aside>

            {/* Form card */}
            <form
              onSubmit={onSubmit}
              className="md:col-span-9 bg-white rounded-3xl border border-brand-line p-6 md:p-10 shadow-sm"
              data-testid="application-form"
            >
              <div className="mb-8">
                <div className="text-[11px] tracking-[0.24em] uppercase text-brand-rose font-semibold">
                  {STEPS[step].label}
                </div>
                <h2 ref={headingRef} tabIndex={-1} className="mt-2 font-serif text-3xl md:text-4xl text-brand-charcoal tracking-tight focus:outline-none">
                  {SECTION_TITLE[STEPS[step].key]}
                </h2>
                {SECTION_BLURB[STEPS[step].key] && (
                  <p className="mt-3 text-brand-muted leading-relaxed">{SECTION_BLURB[STEPS[step].key]}</p>
                )}
              </div>

              {/* Honeypot */}
              <input
                type="text" tabIndex={-1} autoComplete="off" name="website" aria-hidden="true" aria-label="Leave blank"
                value={data.website} onChange={set("website")} className="hidden"
              />

              {/* ----- Step content ----- */}
              {STEPS[step].key === "basic" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="md:col-span-2 rounded-2xl bg-brand-bone/60 border border-brand-line px-5 py-4 text-sm text-brand-charcoal leading-relaxed" data-testid="app-privacy-callout">
                    <span className="font-semibold">Your privacy —</span> some questions ask for sensitive information (Social
                    Security, CDIB and DOC numbers, medications, legal history). Fields without a * are optional — share what
                    you're comfortable with, and you can always provide details later by phone. This information is encrypted
                    and is never sent by email.
                  </div>
                  <Field label="Full name" required>
                    <input className={fieldCls} value={data.name} onChange={set("name")} autoComplete="name" aria-required="true" data-testid="app-name" />
                  </Field>
                  <Field label="Phone" required>
                    <input type="tel" className={fieldCls} value={data.phone} onChange={set("phone")} autoComplete="tel" aria-required="true" data-testid="app-phone" />
                  </Field>
                  <Field label="Email (for confirmation)">
                    <input type="email" className={fieldCls} value={data.email} onChange={set("email")} autoComplete="email" data-testid="app-email" />
                  </Field>
                  <Field label="Date of birth" required>
                    <input type="date" className={fieldCls} value={data.dob} onChange={set("dob")} aria-required="true" data-testid="app-dob" />
                  </Field>
                  <Field label="Address" hint="Current mailing address">
                    <input className={fieldCls} value={data.address} onChange={set("address")} autoComplete="street-address" data-testid="app-address" />
                  </Field>
                  <Field label="Ethnicity">
                    <input className={fieldCls} value={data.ethnicity} onChange={set("ethnicity")} data-testid="app-ethnicity" />
                  </Field>
                  <Field label="Marital status">
                    <select className={selectCls} value={data.marital_status} onChange={set("marital_status")} data-testid="app-marital">
                      <option value="">Select…</option>
                      <option>Single</option><option>Married</option><option>Divorced</option>
                      <option>Separated</option><option>Widowed</option>
                    </select>
                  </Field>
                  <Field label="Referral" hint="Who referred you?">
                    <input className={fieldCls} value={data.referral} onChange={set("referral")} data-testid="app-referral" />
                  </Field>
                  <Field label="Social Security #" hint="Encrypted and visible only to authorized staff">
                    <input className={fieldCls} value={data.ssn} onChange={set("ssn")} autoComplete="off" inputMode="numeric" spellCheck={false} data-testid="app-ssn" />
                  </Field>
                  <Field label="CDIB #">
                    <input className={fieldCls} value={data.cdib_number} onChange={set("cdib_number")} autoComplete="off" data-testid="app-cdib" />
                  </Field>
                  <Field label="DOC #">
                    <input className={fieldCls} value={data.doc_number} onChange={set("doc_number")} autoComplete="off" data-testid="app-doc" />
                  </Field>
                </div>
              )}

              {STEPS[step].key === "housing" && (
                <div className="space-y-5">
                  {data.housing_history.map((row, i) => (
                    <div key={i} className="grid grid-cols-1 md:grid-cols-3 gap-4 p-5 bg-brand-cream rounded-2xl border border-brand-line">
                      <Field label={`Place ${i + 1} — Address`}>
                        <input className={fieldCls} value={row.address} onChange={setNested("housing_history", i, "address")} />
                      </Field>
                      <Field label="When">
                        <input className={fieldCls} placeholder="e.g. 2022–2024" value={row.when} onChange={setNested("housing_history", i, "when")} />
                      </Field>
                      <Field label="With whom">
                        <input className={fieldCls} value={row.with_whom} onChange={setNested("housing_history", i, "with_whom")} />
                      </Field>
                    </div>
                  ))}
                </div>
              )}

              {STEPS[step].key === "connections" && (
                <div className="space-y-5">
                  <Field label="Do you know anyone near Tahlequah, Stilwell, Welling, Park Hill, or Lake Tenkiller?">
                    <select className={selectCls} value={data.knows_local_people} onChange={set("knows_local_people")}>
                      <option value="">Select…</option>
                      <option>Yes</option><option>No</option>
                    </select>
                  </Field>
                  {data.known_contacts.map((row, i) => (
                    <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-4 p-5 bg-brand-cream rounded-2xl border border-brand-line relative">
                      <Field label="Name"><input className={fieldCls} value={row.name} onChange={setNested("known_contacts", i, "name")} /></Field>
                      <Field label="Relation"><input className={fieldCls} value={row.relation} onChange={setNested("known_contacts", i, "relation")} /></Field>
                      <Field label="Address"><input className={fieldCls} value={row.address} onChange={setNested("known_contacts", i, "address")} /></Field>
                      <Field label="Phone"><input className={fieldCls} value={row.phone} onChange={setNested("known_contacts", i, "phone")} /></Field>
                      {data.known_contacts.length > 1 && (
                        <button type="button" onClick={() => removeRow("known_contacts", i)}
                          className="absolute top-3 right-3 text-xs text-brand-rose hover:underline">Remove</button>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={() => addRow("known_contacts", { name: "", relation: "", address: "", phone: "" })}
                    className="text-sm text-brand-teal link-underline" data-testid="add-contact">
                    + Add another contact
                  </button>
                </div>
              )}

              {STEPS[step].key === "medical" && (
                <div className="space-y-4">
                  <p className="text-sm text-brand-muted">List current medications and dosages.</p>
                  {data.medications.map((row, i) => (
                    <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-4 p-5 bg-brand-cream rounded-2xl border border-brand-line relative">
                      <Field label={`Medication ${i + 1}`}>
                        <input className={fieldCls} value={row.name} onChange={setNested("medications", i, "name")} />
                      </Field>
                      <Field label="Dosage">
                        <input className={fieldCls} value={row.dosage} onChange={setNested("medications", i, "dosage")} />
                      </Field>
                      {data.medications.length > 1 && (
                        <button type="button" onClick={() => removeRow("medications", i)}
                          className="absolute top-3 right-3 text-xs text-brand-rose hover:underline">Remove</button>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={() => addRow("medications", { name: "", dosage: "" })}
                    className="text-sm text-brand-teal link-underline" data-testid="add-medication">
                    + Add medication
                  </button>
                </div>
              )}

              {STEPS[step].key === "legal" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Pending charges">
                    <textarea rows={3} className={fieldCls} value={data.pending_charges} onChange={set("pending_charges")} />
                  </Field>
                  <Field label="Criminal history">
                    <textarea rows={3} className={fieldCls} value={data.criminal_history} onChange={set("criminal_history")} />
                  </Field>
                  <Field label="Probation or parole?">
                    <select className={selectCls} value={data.probation_or_parole} onChange={set("probation_or_parole")}>
                      <option value="">Select…</option>
                      <option>Probation</option><option>Parole</option><option>Neither</option>
                    </select>
                  </Field>
                  <Field label="Officer name">
                    <input className={fieldCls} value={data.officer_name} onChange={set("officer_name")} />
                  </Field>
                  <Field label="Officer phone">
                    <input className={fieldCls} value={data.officer_phone} onChange={set("officer_phone")} />
                  </Field>
                </div>
              )}

              {STEPS[step].key === "personal" && (
                <div className="space-y-5">
                  <Field label="What are your personal goals within the next year?">
                    <textarea rows={4} className={fieldCls} value={data.goals_next_year} onChange={set("goals_next_year")} data-testid="app-goals" />
                  </Field>
                  <Field label="How can Hosea Ministries help you in your transition?">
                    <textarea rows={4} className={fieldCls} value={data.how_we_can_help} onChange={set("how_we_can_help")} />
                  </Field>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="Do you have children coming with you or being reunified?">
                      <select className={selectCls} value={data.has_children} onChange={set("has_children")}>
                        <option value="">Select…</option>
                        <option>Yes</option><option>No</option>
                      </select>
                    </Field>
                    <Field label="How many children?">
                      <input className={fieldCls} value={data.num_children} onChange={set("num_children")} />
                    </Field>
                  </div>
                  {data.has_children === "Yes" && (
                    <div className="space-y-4">
                      {data.children.map((row, i) => (
                        <div key={i} className="grid grid-cols-1 md:grid-cols-3 gap-4 p-5 bg-brand-cream rounded-2xl border border-brand-line relative">
                          <Field label={`Child ${i + 1} age`}>
                            <input className={fieldCls} value={row.age} onChange={setNested("children", i, "age")} />
                          </Field>
                          <Field label="With applicant?">
                            <select className={selectCls} value={row.with_applicant} onChange={setNested("children", i, "with_applicant")}>
                              <option value="">Select…</option>
                              <option>Yes</option><option>Reunifying</option>
                            </select>
                          </Field>
                          <Field label="Daycare arranged?">
                            <input className={fieldCls} value={row.daycare} onChange={setNested("children", i, "daycare")} />
                          </Field>
                          {data.children.length > 1 && (
                            <button type="button" onClick={() => removeRow("children", i)}
                              className="absolute top-3 right-3 text-xs text-brand-rose hover:underline">Remove</button>
                          )}
                        </div>
                      ))}
                      <button type="button" onClick={() => addRow("children", { age: "", with_applicant: "", daycare: "" })}
                        className="text-sm text-brand-teal link-underline">+ Add another child</button>
                    </div>
                  )}
                </div>
              )}

              {STEPS[step].key === "references" && (
                <div className="space-y-5">
                  <div className="rounded-2xl bg-brand-bone/60 border border-brand-line px-5 py-4">
                    <p className="text-sm text-brand-charcoal leading-relaxed">
                      <span className="font-semibold">A note on references —</span> we're not necessarily looking
                      for "good" references. We just want a few people who know you. Be honest, and don't worry
                      about it being polished.
                    </p>
                  </div>
                  <p className="text-sm text-brand-muted">Please provide at least 1 reference (3 if you can).</p>
                  {data.references.map((row, i) => (
                    <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-4 p-5 bg-brand-cream rounded-2xl border border-brand-line">
                      <Field label={`Reference ${i + 1} name`}>
                        <input className={fieldCls} value={row.name} onChange={setNested("references", i, "name")} />
                      </Field>
                      <Field label="Phone">
                        <input className={fieldCls} value={row.phone} onChange={setNested("references", i, "phone")} />
                      </Field>
                      <Field label="Address">
                        <input className={fieldCls} value={row.address} onChange={setNested("references", i, "address")} />
                      </Field>
                      <Field label="Relationship">
                        <input className={fieldCls} value={row.relationship} onChange={setNested("references", i, "relationship")} />
                      </Field>
                      <Field label="Years known">
                        <input className={fieldCls} value={row.years_known} onChange={setNested("references", i, "years_known")} />
                      </Field>
                    </div>
                  ))}
                </div>
              )}

              {STEPS[step].key === "employment" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Do you have a job?">
                    <select className={selectCls} value={data.has_job} onChange={set("has_job")}>
                      <option value="">Select…</option>
                      <option>Yes</option><option>No</option>
                    </select>
                  </Field>
                  <Field label="Employer">
                    <input className={fieldCls} value={data.employer} onChange={set("employer")} />
                  </Field>
                  <Field label="Length of employment">
                    <input className={fieldCls} value={data.length_of_employment} onChange={set("length_of_employment")} />
                  </Field>
                  <Field label="Do you have transportation?">
                    <select className={selectCls} value={data.has_transportation} onChange={set("has_transportation")}>
                      <option value="">Select…</option>
                      <option>Yes</option><option>No</option>
                    </select>
                  </Field>
                </div>
              )}

              {STEPS[step].key === "financial" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label={`Will you have your entry fee ($${ORG.fees.entry})?`}>
                    <select className={selectCls} value={data.has_entry_fee} onChange={set("has_entry_fee")}>
                      <option value="">Select…</option>
                      <option>Yes</option><option>No</option>
                    </select>
                  </Field>
                  <Field label="If not, is there family or a friend who can help?">
                    <input className={fieldCls} value={data.family_support} onChange={set("family_support")} />
                  </Field>

                  <div className="md:col-span-2 pt-4 mt-2 border-t border-brand-line">
                    <div className="text-[11px] tracking-[0.24em] uppercase text-brand-rose font-semibold mb-3">Emergency Contact</div>
                  </div>

                  <Field label="Emergency contact name" required>
                    <input className={fieldCls} value={data.emergency_name} onChange={set("emergency_name")} />
                  </Field>
                  <Field label="Emergency contact phone" required>
                    <input className={fieldCls} value={data.emergency_phone} onChange={set("emergency_phone")} />
                  </Field>
                  <Field label="Relationship">
                    <input className={fieldCls} value={data.emergency_relationship} onChange={set("emergency_relationship")} />
                  </Field>
                </div>
              )}

              {STEPS[step].key === "uploads" && (
                <UploadsStep data={data} setData={setData} />
              )}

              {STEPS[step].key === "agreement" && (
                <div className="space-y-6">
                  <div className="bg-brand-cream border border-brand-line rounded-2xl p-6">
                    <div className="text-[11px] tracking-[0.24em] uppercase text-brand-rose font-semibold">Program Rules</div>
                    <h3 className="mt-2 font-serif text-2xl text-brand-charcoal">Alongside house rules, every resident commits to:</h3>
                    <ul className="mt-4 grid sm:grid-cols-2 gap-2 text-sm text-brand-charcoal">
                      {[
                        "Maintain positive attitudes",
                        "Pass weekly and random drug tests",
                        "Obtain employment",
                        "Attend 1 church service weekly",
                        "Attend 1 Bible study weekly",
                        "Attend 1 Celebrate Recovery weekly",
                        "Attend counseling",
                        "Respect the house, others, and spaces",
                        "No foul or offensive language",
                      ].map((e) => (
                        <li key={e} className="flex items-start gap-2"><span className="text-brand-teal mt-0.5">✓</span>{e}</li>
                      ))}
                    </ul>
                    <div className="mt-5 text-sm text-brand-charcoal">
                      <div><span className="font-medium">Entry fee:</span> ${ORG.fees.entry}</div>
                      <div><span className="font-medium">Weekly fee:</span> ${ORG.fees.weekly}</div>
                      <div className="mt-2 text-brand-muted">Please allow 72 hours for our application process.</div>
                    </div>
                  </div>

                  <div className="bg-white border border-brand-line rounded-2xl p-6">
                    <p className="text-sm text-brand-muted leading-relaxed">
                      I certify that the above facts are true to the best of my knowledge and belief and I
                      understand that I subject myself to disciplinary action that can include being released from
                      the program in the event that the above facts are found to be falsified.
                    </p>
                    <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Field label="Type your full name as signature" required>
                        <input className={fieldCls} value={data.signature} onChange={set("signature")} data-testid="app-signature" />
                      </Field>
                      <Field label="Date">
                        <input type="date" className={fieldCls} value={data.signature_date} onChange={set("signature_date")} />
                      </Field>
                    </div>
                    <label className="mt-5 flex items-start gap-3 cursor-pointer select-none">
                      <input
                        type="checkbox" checked={data.agreed_to_rules} onChange={set("agreed_to_rules")}
                        data-testid="app-agree"
                        className="mt-1 w-4 h-4 rounded border-brand-line text-brand-teal focus:ring-brand-teal/30"
                      />
                      <span className="text-sm text-brand-charcoal">
                        I have read and agree to the program rules, expectations, and fees listed above.
                      </span>
                    </label>
                    <label className="mt-4 flex items-start gap-3 cursor-pointer select-none">
                      <input
                        type="checkbox" checked={data.privacy_ack} onChange={set("privacy_ack")}
                        data-testid="app-privacy-ack" aria-describedby="privacy-ack-desc"
                        className="mt-1 w-4 h-4 rounded border-brand-line text-brand-teal focus:ring-brand-teal/30"
                      />
                      <span id="privacy-ack-desc" className="text-sm text-brand-charcoal">
                        I understand this application includes sensitive information (such as my Social Security, CDIB or
                        DOC number, medications, and legal history). I have read the{" "}
                        <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-brand-teal underline underline-offset-4" data-testid="app-privacy-link">Privacy Notice</Link>{" "}
                        and agree that Hosea Ministries Foundation may use it only to review my application and provide program services.
                      </span>
                    </label>
                  </div>
                </div>
              )}

              <p role="alert" aria-live="assertive" className="mt-8 text-sm text-red-700 min-h-[1.25rem]" data-testid="app-error">{error}</p>

              {/* Footer nav */}
              <div className="mt-2 flex items-center justify-between gap-3">
                <button
                  type="button" onClick={prev} disabled={step === 0}
                  data-testid="app-prev"
                  className="inline-flex items-center gap-2 text-brand-muted hover:text-brand-charcoal disabled:opacity-40"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                {step < STEPS.length - 1 ? (
                  <button
                    key="next" type="button" onClick={next} data-testid="app-next"
                    className="inline-flex items-center gap-2 bg-brand-teal text-white rounded-full px-6 py-3 font-medium hover:bg-brand-tealDark transition-all hover:-translate-y-0.5"
                  >
                    Continue <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    key="submit" type="submit" disabled={loading} data-testid="app-submit"
                    className="inline-flex items-center gap-2 bg-brand-teal text-white rounded-full px-7 py-3 font-medium hover:bg-brand-tealDark transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? "Submitting…" : "Submit application"}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

const SECTION_TITLE = {
  basic: "Tell us about yourself",
  housing: "Recent housing history",
  connections: "Local connections",
  medical: "Medical information",
  legal: "Legal history",
  personal: "Your goals & family",
  references: "Three references",
  employment: "Employment & transportation",
  financial: "Financial readiness",
  uploads: "Supporting documents",
  agreement: "Review & sign",
};

const SECTION_BLURB = {
  basic: "We use this to contact you and prepare your file. Everything is kept confidential.",
  housing: "Please list 3 places you've lived in Oklahoma over the past three years.",
  connections: "Anyone you know in or near Tahlequah, Stilwell, Welling, Park Hill, or Lake Tenkiller.",
  medical: "Please list current medications and dosages.",
  legal: "Pending charges, criminal history, and probation/parole information.",
  personal: "Share what you're hoping to accomplish and how we can help.",
  references: "Full details for three personal references.",
  employment: "Your work and transportation situation.",
  financial: "Program fee readiness and your emergency contact.",
  uploads: "Optional — attach any documents that help us process your application faster.",
  agreement: "Please review our program rules and sign to submit.",
};
